import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Download, FileSignature, AlertCircle, Loader, History, Shield, Mail, X, Eye, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isPaidTier } from "@/lib/tiers";
import { fmtDateTime } from "@/lib/datetime";


const STRUCTURES = [
  { id: "seller_carry", label: "Seller Carry" },
  { id: "wraparound", label: "Wraparound" },
  { id: "subject_to", label: "Subject-To" },
  { id: "installment_sale", label: "Installment" },
  { id: "lease_option", label: "Lease Option" },
];

const CATEGORY_ORDER = ["Negotiation", "Agreements", "Due Diligence", "Creative Finance", "Presentations"];


export default function DocumentTemplatesPanel({ opp }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPaidAi = !!user && (["admin", "super_admin"].includes(user.role) ||
    (isPaidTier(user.tier) && user.subscription_status !== "canceled"));
  const [grouped, setGrouped] = useState({});
  const [generated, setGenerated] = useState([]);
  const [busy, setBusy] = useState({});
  const [addendumStructure, setAddendumStructure] = useState("seller_carry");
  const [docusignConnected, setDocusignConnected] = useState(false);
  const [brevoConfigured, setBrevoConfigured] = useState(false);
  const [profileReady, setProfileReady] = useState({ ready_for_documents: true, missing_required: [] });
  const [stateMeta, setStateMeta] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [emailModal, setEmailModal] = useState(null); // {template_key, title}
  const [previewModal, setPreviewModal] = useState(null); // {template_key, title, blobUrl, busy}
  // Iter 59 Task 2 ,  DocuSign confirmation modal state.
  // { template_key, title, recipient_email, recipient_name, previewBlobUrl, previewBusy }
  const [docusignConfirm, setDocusignConfirm] = useState(null);

  const load = async () => {
    try {
      const calls = [
        api.get("/documents/templates"),
        api.get(`/documents/list/${opp.oid}`),
        api.get("/docusign/status"),
        api.get("/documents/profile-ready"),
        api.get("/integrations/me"),
      ];
      if (opp?.state) calls.push(api.get(`/documents/state-psa/${opp.state}`));
      const [t, g, d, pr, ig, sm] = await Promise.all(calls);
      setGrouped(t.data.grouped || {});
      setGenerated(g.data.documents || []);
      setDocusignConnected(d.data.connected);
      setProfileReady(pr.data);
      setBrevoConfigured(!!ig.data?.brevo?.configured);
      if (sm) setStateMeta(sm.data);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => { if (opp?.oid) load(); /* eslint-disable-next-line */ }, [opp?.oid, opp?.state]);

  // Fetch a stored generated_documents PDF (including DocuSign-signed copies)
  // via /documents/generated/view and open it in a new tab. Mirrors the working
  // pattern in FollowUpCadencePanel.jsx (lines 199-212) so both entry points
  // share the same server-authenticated blob flow.
  const openSignedDoc = (d) => {
    if (!d?.created_at) {
      toast.error("Document unavailable");
      return;
    }
    const token = localStorage.getItem("ae_token") || "";
    const params = new URLSearchParams({ oid: d.oid || opp.oid, created_at: d.created_at });
    fetch(`${API}/documents/generated/view?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error("Unable to open signed document"));
  };

  const fmtBytes = (n) => {
    if (!n || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  const renderDoc = async (key, format) => {
    if (!profileReady.ready_for_documents) {
      toast.error("Complete your profile (Settings) before generating documents.");
      return;
    }
    setBusy((b) => ({ ...b, [`${key}_${format}`]: true }));
    try {
      const terms = key === "addendum" ? { structure: addendumStructure } : {};
      const r = await api.post("/documents/render",
        { oid: opp.oid, template_key: key, format, terms },
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], {
        type: format === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${key.toUpperCase()} ${format.toUpperCase()} downloaded`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "profile_incomplete") {
        toast.error("Complete your profile before generating documents.");
        setProfileReady({ ready_for_documents: false, missing_required: detail.missing_required });
      } else {
        toast.error(typeof detail === "string" ? detail : "Render failed");
      }
    } finally {
      setBusy((b) => ({ ...b, [`${key}_${format}`]: false }));
    }
  };

  const sendDocusign = async (key) => {
    if (!docusignConnected) {
      toast.error("Connect your DocuSign account in Settings first.");
      return;
    }
    if (!opp.owner_email || !opp.owner_name) {
      toast.error("Opportunity is missing owner_email or owner_name.");
      return;
    }
    // Iter 59 Task 2 ,  open confirmation modal instead of sending immediately.
    const title = (grouped && Object.values(grouped).flat().find((t) => t.key === key)?.title) || key;
    setDocusignConfirm({
      template_key: key,
      title,
      recipient_email: opp.owner_email,
      recipient_name: opp.owner_name,
      previewBlobUrl: null,
      previewBusy: true,
    });
    // Fetch a preview PDF in the background so the subscriber can visually
    // verify the document before dispatching.
    try {
      const terms = key === "addendum" ? { structure: addendumStructure } : {};
      const r = await api.post("/documents/render",
        { oid: opp.oid, template_key: key, format: "pdf", terms },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setDocusignConfirm((prev) => (prev && prev.template_key === key ? { ...prev, previewBlobUrl: url, previewBusy: false } : prev));
    } catch {
      setDocusignConfirm((prev) => (prev && prev.template_key === key ? { ...prev, previewBusy: false } : prev));
    }
  };

  const confirmDocusignSend = async () => {
    if (!docusignConfirm) return;
    const { template_key, recipient_email, recipient_name } = docusignConfirm;
    setBusy((b) => ({ ...b, [`${template_key}_ds`]: true }));
    try {
      const terms = template_key === "addendum" ? { structure: addendumStructure } : {};
      const r = await api.post("/documents/send-via-docusign", {
        oid: opp.oid,
        template_key,
        terms,
        recipient_email,
        recipient_name,
      });
      toast.success(`DocuSign envelope ${r.data.envelope_id} sent (${r.data.status})`);
      if (docusignConfirm.previewBlobUrl) URL.revokeObjectURL(docusignConfirm.previewBlobUrl);
      setDocusignConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DocuSign send failed");
    } finally {
      setBusy((b) => ({ ...b, [`${template_key}_ds`]: false }));
    }
  };

  const cancelDocusignConfirm = () => {
    if (docusignConfirm?.previewBlobUrl) URL.revokeObjectURL(docusignConfirm.previewBlobUrl);
    setDocusignConfirm(null);
  };

  const openEmailModal = (template_key, title) => {
    if (!profileReady.ready_for_documents) {
      toast.error("Complete your profile (Settings) before sending documents.");
      return;
    }
    setEmailModal({
      template_key,
      title,
      recipient_email: opp.owner_email || "",
      recipient_name: opp.owner_name || "",
      custom_subject: title ? `${title} ,  ${opp.address || opp.title || ""}`.trim() : "",
      custom_message: "",
    });
  };

  const openPreview = async (template_key, title) => {
    if (!profileReady.ready_for_documents) {
      toast.error("Complete your profile (Settings) before previewing documents.");
      return;
    }
    setPreviewModal({ template_key, title, blobUrl: null, busy: true });
    setBusy((b) => ({ ...b, [`${template_key}_preview`]: true }));
    try {
      const terms = template_key === "addendum" ? { structure: addendumStructure } : {};
      const r = await api.post("/documents/render",
        { oid: opp.oid, template_key, format: "pdf", terms },
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewModal({ template_key, title, blobUrl: url, busy: false });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "profile_incomplete") {
        toast.error("Complete your profile before generating documents.");
        setProfileReady({ ready_for_documents: false, missing_required: detail.missing_required });
      } else {
        toast.error(typeof detail === "string" ? detail : "Preview failed");
      }
      setPreviewModal(null);
    } finally {
      setBusy((b) => ({ ...b, [`${template_key}_preview`]: false }));
    }
  };

  const closePreview = () => {
    if (previewModal?.blobUrl) URL.revokeObjectURL(previewModal.blobUrl);
    setPreviewModal(null);
  };

  const sendEmailFromPreview = () => {
    if (!previewModal) return;
    const tk = previewModal.template_key;
    const t = previewModal.title;
    closePreview();
    openEmailModal(tk, t);
  };

  // Hand the current template + opportunity to the AI Acquisition Assistant
  // for editing. The Assistant reads sessionStorage on mount, auto-opens the
  // Branded Document Export drawer, selects the right template button, and
  // pre-populates the WysiwygEditor via /api/documents/preview.
  const editInAssistant = () => {
    if (!previewModal) return;
    if (!isPaidAi) {
      toast.error("The AI Acquisition Assistant is a Pro feature. Upgrade to edit documents in the Assistant.", {
        action: { label: "Upgrade", onClick: () => navigate("/app/billing") },
      });
      return;
    }
    try {
      sessionStorage.setItem("acquigine_doc_handoff", JSON.stringify({
        oid: opp.oid,
        template_key: previewModal.template_key,
        title: previewModal.title,
        from: "opportunity_detail",
        timestamp: Date.now(),
      }));
    } catch {/* ignore quota errors */}
    closePreview();
    toast.success("Opening in AI Assistant for editing…");
    navigate("/app/assistant");
  };


  const sendEmail = async () => {
    if (!emailModal) return;
    const { template_key, recipient_email, recipient_name, custom_subject, custom_message } = emailModal;
    if (!recipient_email || !recipient_name) {
      toast.error("Recipient email and name are required.");
      return;
    }
    setBusy((b) => ({ ...b, [`${template_key}_email`]: true }));
    try {
      const terms = template_key === "addendum" ? { structure: addendumStructure } : {};
      const r = await api.post("/documents/send-via-email", {
        oid: opp.oid,
        template_key,
        terms,
        recipient_email,
        recipient_name,
        custom_subject: custom_subject || undefined,
        custom_message: custom_message || undefined,
      });
      toast.success(`Email sent to ${r.data.recipient}`);
      setEmailModal(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "brevo_not_connected") {
        toast.error("Connect your Brevo account in Settings → Integrations.");
      } else if (detail?.error === "profile_incomplete") {
        toast.error("Complete your profile before sending documents.");
        setProfileReady({ ready_for_documents: false, missing_required: detail.missing_required });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Email send failed"));
      }
    } finally {
      setBusy((b) => ({ ...b, [`${template_key}_email`]: false }));
    }
  };

  const orderedCats = CATEGORY_ORDER.filter((c) => grouped[c]?.length > 0);
  const totalCount = Object.values(grouped).reduce((acc, arr) => acc + arr.length, 0);

  return (
    <div className="ae-card p-5 space-y-4" data-testid="documents-templates-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-emerald-400" />
          <h3 className="font-heading text-lg">Document Generator</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{totalCount} templates</span>
      </div>

      {/* Profile-completeness block */}
      {!profileReady.ready_for_documents && (
        <div className="text-xs text-rose-100 bg-rose-500/10 border border-rose-500/40 rounded-sm p-3 space-y-1.5"
             data-testid="profile-incomplete-warning">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-rose-300" />
            <div>
              <div className="font-medium text-rose-200">Complete your profile before generating documents.</div>
              <div className="text-rose-100/70 mt-1">
                Your name and contact information will appear on all documents as the preparer.
                Missing: {profileReady.missing_required.join(", ")}.
              </div>
              <Link to="/app/settings" className="inline-block mt-2 text-emerald-300 hover:underline">Go to Settings &rarr;</Link>
            </div>
          </div>
        </div>
      )}

      {/* Attorney-state warning */}
      {stateMeta?.attorney_required && (
        <div className="text-xs text-amber-100 bg-amber-500/10 border border-amber-500/40 rounded-sm p-3"
             data-testid="attorney-state-warning">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 mt-0.5 shrink-0 text-amber-300" />
            <div>
              <div className="font-medium text-amber-200">{stateMeta.state_name} is an attorney-closing state.</div>
              <div className="text-amber-100/80 mt-1">
                A licensed real estate attorney must review and conduct the closing for this transaction.
                Acquigine documents are templates only and do not substitute for legal counsel.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Standard-template badge */}
      {stateMeta && (
        <div className="flex items-center gap-2 text-xs">
          <span data-testid="template-status-badge"
                className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                  stateMeta.template_status === "Full"
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                    : "bg-sky-500/15 text-sky-300 border-sky-500/40"
                }`}
                title={
                  stateMeta.template_status === "Standard"
                    ? `Standard template with ${stateMeta.state_name}-specific disclosures. Recommended for review by a local real estate attorney before execution.`
                    : `Full template for ${stateMeta.state_name}.`
                }>
            {stateMeta.template_status} template
          </span>
          <span className="text-slate-500">
            {stateMeta.state_name} - {stateMeta.deed_type} - {stateMeta.closing_by}
          </span>
        </div>
      )}

      {/* DocuSign banner */}
      {!docusignConnected && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm p-2 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>Connect DocuSign in <Link to="/app/settings" className="text-emerald-400 hover:underline">Settings</Link> to send documents for e-signature.</div>
        </div>
      )}

      {/* Grouped templates */}
      <div className="space-y-4" data-testid="documents-list">
        {orderedCats.map((cat) => (
          <div key={cat}>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">{cat}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {grouped[cat].map((t) => {
                const isAddendum = t.key === "addendum";
                return (
                  <div key={t.key} className="border border-white/8 rounded-sm bg-white/2 p-3 flex flex-col"
                       data-testid={`doc-template-${t.key}`}>
                    <div className="text-sm font-medium text-slate-200 leading-tight">{t.label}</div>
                    <div className="text-[11px] text-slate-500 mt-2 leading-snug line-clamp-3">{t.description}</div>
                    {isAddendum && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {STRUCTURES.map((s) => (
                          <button key={s.id} onClick={() => setAddendumStructure(s.id)}
                                  data-testid={`addendum-structure-${s.id}`}
                                  className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border ${
                                    addendumStructure === s.id ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                                                              : "bg-white/0 text-slate-400 border-white/10"
                                  }`}>{s.label}</button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => openPreview(t.key, t.label)}
                            disabled={busy[`${t.key}_preview`] || !profileReady.ready_for_documents}
                            data-testid={`doc-preview-${t.key}`}
                            title="Preview rendered PDF - download, e-sign, or email from the preview"
                            className="mt-auto px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border bg-white/5 text-slate-200 border-white/15 hover:bg-white/10 hover:border-white/30 disabled:opacity-40 inline-flex items-center justify-center gap-1 self-start">
                      {busy[`${t.key}_preview`]
                        ? <Loader className="h-3 w-3 animate-spin" />
                        : <Eye className="h-3 w-3" />}
                      Preview
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Document History */}
      {generated.length > 0 && (
        <div className="pt-3 border-t border-white/8" data-testid="document-history-panel">
          <button onClick={() => setHistoryOpen(!historyOpen)}
                  className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-200 w-full">
            <History className="h-3 w-3" />
            Document History ({generated.length})
            <span className="ml-auto">{historyOpen ? "Hide" : "Show"}</span>
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-1 max-h-72 overflow-y-auto">
              {generated.map((d, i) => {
                const isSigned = d.signed === true || d.docusign_status === "completed";
                return (
                <div key={i} className="text-xs text-slate-400 flex items-baseline justify-between gap-2 py-1 border-b border-white/5"
                     data-testid={`history-row-${i}`}>
                  <span className="truncate flex-1">{d.title}</span>
                  <span className="text-slate-500 shrink-0 font-mono flex items-center gap-2">
                    {d.docusign_envelope_id ? (
                      <>
                        <span className="text-emerald-400" title={d.docusign_envelope_id}>
                          envelope {d.docusign_envelope_id.slice(0, 8)}
                        </span>
                        {isSigned ? (
                          <>
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 text-[10px] uppercase tracking-wider"
                              data-testid={`history-row-${i}-signed-pill`}
                              title={`Signed and stored ${d.signed_pdf_downloaded_at || ""}`}
                            >
                              <FileSignature className="h-2.5 w-2.5" />
                              Signed
                            </span>
                            {d.signed_pdf_size_bytes ? (
                              <span
                                className="text-emerald-300/80 text-[10px]"
                                data-testid={`history-row-${i}-signed-size`}
                              >
                                {fmtBytes(d.signed_pdf_size_bytes)}
                              </span>
                            ) : null}
                            <button
                              onClick={() => openSignedDoc(d)}
                              className="inline-flex items-center gap-1 px-2 py-[2px] rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200 transition text-[10px] uppercase tracking-wider"
                              data-testid={`history-row-${i}-download-signed`}
                              title="Open the signed PDF in a new tab"
                            >
                              <Download className="h-2.5 w-2.5" />
                              Signed PDF
                            </button>
                          </>
                        ) : (
                          <span
                            className="text-slate-500 text-[10px] uppercase tracking-wider"
                            data-testid={`history-row-${i}-envelope-status`}
                          >
                            {d.docusign_status || "sent"}
                          </span>
                        )}
                      </>
                    ) : d.email_provider === "brevo" ? (
                      <>
                        <span className="text-cyan-400" title={d.email_message_id}>email → {d.email_sent_to}</span>
                        <EmailStatusBadge doc={d} />
                      </>
                    ) : (
                      d.format?.toUpperCase()
                    )}
                    <span>{new Date(d.created_at).toLocaleString(undefined, { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* Preview modal: shows the rendered PDF inline before download/send */}
      {previewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
             onClick={closePreview}
             data-testid="preview-modal-overlay">
          <div className="ae-card w-full max-w-5xl h-full max-h-[95vh] flex flex-col bg-[#0A0D12] overflow-hidden"
               onClick={(e) => e.stopPropagation()}
               data-testid="preview-modal">
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/8">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                  <Eye className="h-3 w-3" /> Document Preview
                </div>
                <div className="font-heading text-lg text-slate-100 mt-1">{previewModal.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Branded with your subscriber identity · review before download or send
                </div>
              </div>
              <button onClick={closePreview} className="text-slate-500 hover:text-slate-200"
                      data-testid="preview-modal-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 bg-[#05070A]">
              {previewModal.busy || !previewModal.blobUrl ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm"
                     data-testid="preview-modal-loading">
                  <Loader className="h-4 w-4 animate-spin mr-2" /> Rendering PDF…
                </div>
              ) : (
                <iframe
                  src={previewModal.blobUrl}
                  title="Document preview"
                  data-testid="preview-modal-iframe"
                  className="w-full bg-white shadow-2xl border border-white/10 rounded-sm"
                  style={{ height: "100%", minHeight: "70vh" }}
                />
              )}
            </div>

            <div className="flex flex-wrap gap-2 justify-end items-center px-5 py-3 border-t border-white/8 bg-[#0A0D12]">
              <div className="mr-auto text-[10px] uppercase tracking-widest text-slate-500">
                {previewModal.template_key}
              </div>
              <button
                onClick={editInAssistant}
                data-testid="preview-modal-edit"
                title={isPaidAi ? "Open in AI Acquisition Assistant for editing" : "Upgrade to Pro to edit in AI Assistant"}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${
                  isPaidAi
                    ? "bg-violet-500/15 text-violet-200 border-violet-500/40 hover:bg-violet-500/25"
                    : "bg-white/5 text-slate-400 border-white/15 hover:bg-white/10"
                }`}
              >
                {isPaidAi ? <Pencil className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                {isPaidAi ? "Edit" : "Edit (Pro)"}
              </button>
              <button
                onClick={() => renderDoc(previewModal.template_key, "pdf")}
                disabled={busy[`${previewModal.template_key}_pdf`]}
                data-testid="preview-modal-download-pdf"
                className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <Download className="h-3 w-3" /> Download PDF
              </button>
              <button
                onClick={() => renderDoc(previewModal.template_key, "docx")}
                disabled={busy[`${previewModal.template_key}_docx`]}
                data-testid="preview-modal-download-docx"
                className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <Download className="h-3 w-3" /> Download DOCX
              </button>
              <button
                onClick={() => sendDocusign(previewModal.template_key)}
                disabled={busy[`${previewModal.template_key}_ds`] || !docusignConnected}
                title={!docusignConnected ? "Connect DocuSign in Settings first" : ""}
                data-testid="preview-modal-send-docusign"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {busy[`${previewModal.template_key}_ds`]
                  ? <Loader className="h-3 w-3 animate-spin" />
                  : <FileSignature className="h-3 w-3" />}
                Send via DocuSign
              </button>
              <button
                onClick={sendEmailFromPreview}
                data-testid="preview-modal-send-email"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-cyan-500/15 text-cyan-200 border-cyan-500/50 hover:bg-cyan-500/25 inline-flex items-center gap-1.5"
              >
                <Mail className="h-3 w-3" /> Send via Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send-via-Email modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={() => setEmailModal(null)}
             data-testid="email-modal-overlay">
          <div className="ae-card max-w-lg w-full p-5 space-y-4 bg-[#0A0D12] max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}
               data-testid="email-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 flex items-center gap-1.5">
                  <Mail className="h-3 w-3" /> Send via Email
                </div>
                <div className="font-heading text-lg text-slate-100 mt-1">{emailModal.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Sent via your Brevo account · PDF attached · CAN-SPAM footer
                </div>
              </div>
              <button onClick={() => setEmailModal(null)} className="text-slate-500 hover:text-slate-300"
                      data-testid="email-modal-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            {!brevoConfigured && (
              <div className="border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs p-3 rounded-sm flex items-start gap-2"
                   data-testid="email-modal-brevo-warning">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  Your Brevo account isn&apos;t connected. Send will fall back to the platform&apos;s limited sender.{" "}
                  <Link to="/app/settings" className="underline text-amber-100">Connect in Settings →</Link>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Recipient name</label>
              <input
                type="text"
                value={emailModal.recipient_name}
                onChange={(e) => setEmailModal({ ...emailModal, recipient_name: e.target.value })}
                data-testid="email-recipient-name"
                className="w-full bg-[#05070A] border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-emerald-500/60 outline-none"
                placeholder="Jane Owner"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Recipient email</label>
              <input
                type="email"
                value={emailModal.recipient_email}
                onChange={(e) => setEmailModal({ ...emailModal, recipient_email: e.target.value })}
                data-testid="email-recipient-email"
                className="w-full bg-[#05070A] border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-emerald-500/60 outline-none"
                placeholder="jane@example.com"
              />
              {!opp.owner_email && (
                <div className="text-[10px] text-amber-400/80" data-testid="email-modal-no-owner-hint">
                  No owner email on file for this opportunity. Enter it manually above or add it via Edit Opportunity to prefill next time.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Subject</label>
              <input
                type="text"
                value={emailModal.custom_subject}
                onChange={(e) => setEmailModal({ ...emailModal, custom_subject: e.target.value })}
                data-testid="email-subject"
                className="w-full bg-[#05070A] border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-emerald-500/60 outline-none"
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">
                Personal message <span className="text-slate-600 normal-case">(optional, HTML allowed)</span>
              </label>
              <textarea
                rows={5}
                value={emailModal.custom_message}
                onChange={(e) => setEmailModal({ ...emailModal, custom_message: e.target.value })}
                data-testid="email-custom-message"
                placeholder="Leave blank to use the default cover note."
                className="w-full bg-[#05070A] border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-emerald-500/60 outline-none font-mono"
              />
            </div>

            <div className="text-[10px] text-slate-500 border-t border-white/8 pt-3">
              Your signature, postal address, and an UNSUBSCRIBE reply path are auto-appended in the footer.
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEmailModal(null)}
                      data-testid="email-modal-cancel"
                      className="btn-ghost text-xs">
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={busy[`${emailModal.template_key}_email`]}
                data-testid="email-modal-send"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-cyan-500/15 text-cyan-200 border-cyan-500/50 hover:bg-cyan-500/25 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy[`${emailModal.template_key}_email`]
                  ? <Loader className="h-3 w-3 animate-spin" />
                  : <Mail className="h-3 w-3" />}
                Send PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter 59 Task 2 ,  DocuSign send confirmation modal */}
      {docusignConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="docusign-confirm-modal">
          <div className="ae-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">Confirm DocuSign send</div>
                <div className="font-heading text-xl text-slate-100">Review before dispatching</div>
                <p className="text-xs text-slate-400 mt-1">
                  This sends a signable envelope. Once sent, DocuSign notifies the recipient immediately &mdash; this action cannot be undone.
                </p>
              </div>
              <button
                onClick={cancelDocusignConfirm}
                className="text-slate-400 hover:text-slate-100"
                data-testid="docusign-confirm-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 border border-white/10 rounded-sm p-3 bg-slate-800/40 text-xs mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Recipient name</div>
                <div className="font-mono text-slate-100 mt-1" data-testid="docusign-confirm-recipient-name">{docusignConfirm.recipient_name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Recipient email</div>
                <div className="font-mono text-slate-100 mt-1" data-testid="docusign-confirm-recipient-email">{docusignConfirm.recipient_email}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Document</div>
                <div className="text-slate-100 mt-1" data-testid="docusign-confirm-title">{docusignConfirm.title}</div>
              </div>
            </div>

            <div className="border border-white/10 rounded-sm bg-black/40 mb-4 overflow-hidden" style={{height: "360px"}}>
              {docusignConfirm.previewBusy || !docusignConfirm.previewBlobUrl ? (
                <div className="h-full flex items-center justify-center text-xs text-slate-500 gap-2">
                  <Loader className="h-3 w-3 animate-spin" /> Rendering preview...
                </div>
              ) : (
                <iframe
                  data-testid="docusign-confirm-preview"
                  title="DocuSign document preview"
                  src={docusignConfirm.previewBlobUrl}
                  className="w-full h-full"
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={cancelDocusignConfirm}
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border border-white/10 text-slate-300 hover:bg-white/5"
                data-testid="docusign-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDocusignSend}
                disabled={busy[`${docusignConfirm.template_key}_ds`]}
                className="px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-200 border-emerald-500/50 hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="docusign-confirm-send-now"
              >
                {busy[`${docusignConfirm.template_key}_ds`]
                  ? <Loader className="h-3 w-3 animate-spin" />
                  : <FileSignature className="h-3 w-3" />}
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Color/label config for Brevo webhook-derived email status badges in the
// document history list.
const EMAIL_STATUS_META = {
  requested:    { label: "Sent",         color: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  deferred:     { label: "Deferred",     color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  delivered:    { label: "✓ Delivered",  color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  opened:       { label: "👁 Opened",    color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  clicked:      { label: "🖱 Clicked",   color: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  soft_bounce:  { label: "⚠ Soft bounce", color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  bounced:      { label: "✕ Bounced",    color: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  spam:         { label: "🚫 Spam",      color: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  unsubscribed: { label: "Unsubscribed",  color: "bg-slate-500/15 text-slate-400 border-slate-500/40" },
  error:        { label: "✕ Error",       color: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};

function EmailStatusBadge({ doc }) {
  const status = doc.email_status;
  if (!status) {
    return (
      <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border bg-white/5 text-slate-500 border-white/10"
            title="No delivery events yet ,  configure Brevo webhook to track delivery">
        Pending
      </span>
    );
  }
  const meta = EMAIL_STATUS_META[status] || {
    label: status,
    color: "bg-white/5 text-slate-400 border-white/10",
  };
  const tooltip = [
    doc.email_delivered_at && `Delivered: ${fmtDateTime(doc.email_delivered_at)}`,
    doc.email_first_opened_at && `Opened: ${fmtDateTime(doc.email_first_opened_at)}`,
    doc.email_first_clicked_at && `Clicked: ${fmtDateTime(doc.email_first_clicked_at)}`,
    doc.email_status_updated_at && `Updated: ${fmtDateTime(doc.email_status_updated_at)}`,
  ].filter(Boolean).join(" · ");
  return (
    <span
      className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border ${meta.color}`}
      data-testid={`email-status-${status}`}
      title={tooltip}
    >
      {meta.label}
    </span>
  );
}

